import React, { useState, useMemo } from 'react';
import { ConfigData, Order } from '../lib/types';
import { exportAllOrders } from '../lib/api';
import { EmptyState } from './ui';
import { ReportsFilterBar, ReportType } from './reports/ReportsFilterBar';
import { ReportsOutput } from './reports/ReportsOutput';
import { buildCsv } from './reports/csvExport';

// =============================================================================
// ReportsTab — orchestrator for reports feature
//   State + filter logic live here; filter bar + output are dedicated components.
//   CSV/PDF export: CSV pulls full dataset via export_all; PDF uses window.print().
// =============================================================================

interface ReportsTabProps {
  configData: ConfigData | null;
  orders?: Order[];
  locale?: string;
  currency?: string;
}

export function ReportsTab({ configData, orders = [], locale = 'en-NZ', currency = '$' }: ReportsTabProps) {
  const [reportType, setReportType]   = useState<ReportType>('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [deptFilter, setDeptFilter]   = useState('all');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [exporting, setExporting]     = useState(false);

  const activeDepts = (configData?.departments ?? []).filter(d => d.active !== false);
  const activeStaff = (configData?.directory ?? []).filter(s => s.active !== false);

  const filteredOrders = useMemo(() => orders.filter(o => {
    if (staffFilter !== 'all' && o.staff_name !== staffFilter) return false;
    if (deptFilter !== 'all' && o.department_id !== deptFilter) return false;
    if (dateFrom && o.order_date < dateFrom) return false;
    if (dateTo && o.order_date > dateTo) return false;
    return true;
  }), [orders, staffFilter, deptFilter, dateFrom, dateTo]);

  const totalSpend = filteredOrders.reduce((s, o) => s + (o.total_cost || 0), 0);

  async function exportCSV() {
    setExporting(true);
    const res = await exportAllOrders({
      department_id: deptFilter !== 'all' ? deptFilter : undefined,
      date_from: dateFrom || undefined,
      date_to:   dateTo   || undefined,
    });
    if (!res.ok) {
      setExporting(false);
      alert(res.message || 'Export failed. Please try again.');
      return;
    }
    let rows = res.data || [];
    if (staffFilter !== 'all') rows = rows.filter(o => o.staff_name === staffFilter);
    const total = rows.reduce((s, o) => s + (o.total_cost || 0), 0);
    const csv = buildCsv(reportType, rows, total);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gopal-report-${reportType}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExporting(false);
    if (res.truncated) {
      alert(`Export contains the first ${res.truncated_at ?? rows.length} rows only — please narrow your filters to cover the full dataset.`);
    }
  }

  return (
    <div className="pane active">
      <ReportsFilterBar
        reportType={reportType} setReportType={setReportType}
        staffFilter={staffFilter} setStaffFilter={setStaffFilter}
        deptFilter={deptFilter} setDeptFilter={setDeptFilter}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        activeStaff={activeStaff}
        activeDepts={activeDepts}
        exporting={exporting}
        onExportCSV={exportCSV}
        onPrint={() => window.print()}
      />

      <div id="rep-out" className="tw" style={{ marginBottom: 16 }}>
        {filteredOrders.length === 0 ? (
          <EmptyState
            icon={
              <svg viewBox="0 0 32 32" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 4H8a2 2 0 00-2 2v20a2 2 0 002 2h16a2 2 0 002-2V10z" />
                <path d="M20 4v6h6M12 18h8M12 22h5" />
              </svg>
            }
            title="No orders match your filters"
            description="Try adjusting the date range or removing filters. Reports are generated from the current working set of orders."
          />
        ) : (
          <ReportsOutput
            reportType={reportType}
            filteredOrders={filteredOrders}
            totalSpend={totalSpend}
            currency={currency}
            locale={locale}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        )}
      </div>
    </div>
  );
}
